import { QueryResult } from '@apollo/client';
import { pathOr } from 'ramda';
import React from 'react';

import { CommonFormConfigureForecastProps, FormConfigureForecast } from './FormConfigureForecast';
import {
  CalculateForecastComponent,
  CalculateForecastQuery,
  CalculateForecastQueryVariables,
  ForecastInfoInput,
  GetForecastComponent,
  GetForecastQuery,
  GetForecastQueryVariables,
} from '../../../graphql/components';

type FormConfigureForecastContainerProps = CommonFormConfigureForecastProps & {
  parameters: ForecastInfoInput;
};

export const FormConfigureForecastContainer: React.FC<FormConfigureForecastContainerProps> = (props) => {
  const {
    adjustments,
    initializeAdjustments,
    isEdit,
    isView,
    forecastCreatedUpdated,
    parameters,
    parametersChanged,
    values: { id = '' },
  } = props;

  const shouldUseGetForecast = isView || (isEdit && !forecastCreatedUpdated && !parametersChanged);

  return (
    <GetForecastComponent
      skip={!shouldUseGetForecast || !id}
      variables={{
        id,
      }}
      fetchPolicy="no-cache" // Having cache enabled would result in inconsistent data within scene!
    >
      {(getForecastResult: QueryResult<GetForecastQuery, GetForecastQueryVariables>) => (
        <CalculateForecastComponent
          skip={shouldUseGetForecast}
          variables={{ parameters: { adjustments, info: parameters } }}
          fetchPolicy="no-cache" // Having cache enabled would result in inconsistent data within scene!
        >
          {(calculateForecastResult: QueryResult<CalculateForecastQuery, CalculateForecastQueryVariables>) => {
            const getForecastData = pathOr({}, ['data', 'forecast', 'values'], getForecastResult);
            const getForecastAdjustments = pathOr([], ['data', 'forecast', 'adjustments'], getForecastResult);
            const calculateForecastData = pathOr({}, ['data', 'calculatedForecast'], calculateForecastResult);

            if (getForecastAdjustments.length && !parametersChanged && !adjustments.length) {
              initializeAdjustments(getForecastAdjustments);
            }

            const forecastData = shouldUseGetForecast ? getForecastData : calculateForecastData;
            const adjustmentsToSend = shouldUseGetForecast ? getForecastAdjustments : adjustments;

            const loading = getForecastResult.loading || calculateForecastResult.loading;
            const error = getForecastResult.error || calculateForecastResult.error;

            return (
              <FormConfigureForecast
                {...props}
                adjustments={adjustmentsToSend}
                error={!!error}
                forecastData={forecastData}
                loading={loading}
              />
            );
          }}
        </CalculateForecastComponent>
      )}
    </GetForecastComponent>
  );
};
